import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Bell, Menu } from 'lucide-react';
import { useUIStore } from '../../stores/uiStore';
import { NotificationMenu } from './NotificationMenu';
import { useDashboard } from '../../features/dashboard/hooks/useDashboard';

interface HeaderProps {
  onMenuClick?: () => void;
}

const PAGE_TITLES: Record<string, string> = {
  '/': 'ホーム',
  '/chatbots': 'チャットボット',
  '/subscription': 'プラン',
  '/settings': '設定',
  '/faq': 'ヘルプ',
  '/notifications': '通知',
  '/contact': 'お問い合わせ',
};

const getPageTitle = (pathname: string): string => {
  if (pathname.startsWith('/chatbots/') && pathname !== '/chatbots/new') return 'BOT詳細';
  return PAGE_TITLES[pathname] ?? '';
};

export const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { sidebarCollapsed } = useUIStore();
  const { unreadCount } = useDashboard();
  const location = useLocation();

  const [notificationMenuOpen, setNotificationMenuOpen] = useState(false);
  const notificationRef = useRef<HTMLDivElement>(null);

  const pageTitle = getPageTitle(location.pathname);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setNotificationMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header
      data-testid="header"
      className={[
        'fixed top-0 right-0 z-40 bg-white border-b border-border h-16',
        'flex items-center px-4 gap-1 transition-all duration-200',
        sidebarCollapsed ? 'left-0 md:left-[72px]' : 'left-0 md:left-60',
      ].join(' ')}
    >
      {/* モバイル: ハンバーガーメニュー */}
      <button
        onClick={onMenuClick}
        aria-label="メニュー"
        data-testid="sidebar-toggle-mobile"
        className="md:hidden flex items-center justify-center w-11 h-11 rounded-md text-text-secondary hover:bg-gray-100 transition-colors"
      >
        <Menu size={20} />
      </button>

      {/* ページタイトル */}
      {pageTitle && (
        <h1 className="text-lg font-bold text-[#0F172A] ml-1">{pageTitle}</h1>
      )}

      {/* フレキシブルスペース */}
      <div className="flex-1" />

      {/* 通知アイコン */}
      <div className="relative" ref={notificationRef}>
        <button
          onClick={() => setNotificationMenuOpen(!notificationMenuOpen)}
          title="通知"
          aria-label="通知"
          data-testid="header-notification-icon"
          className="relative flex items-center justify-center w-9 h-9 text-text-secondary rounded-[8px] bg-[#F8FAFC] hover:bg-gray-100 transition-colors"
        >
          <Bell size={20} />
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
          )}
        </button>
        <NotificationMenu open={notificationMenuOpen} onClose={() => setNotificationMenuOpen(false)} />
      </div>

    </header>
  );
};
